/* eslint-disable no-use-before-define */
import { get } from "./io";

let credentialsCache = null;
let credentialsPromise = null;

async function getCredentials() {
  if (credentialsCache) return credentialsCache;
  if (credentialsPromise) return credentialsPromise;

  credentialsPromise = get("iot/ws-credentials")
    .then((res) => {
      if (res.status !== 200)
        throw new Error("Failed to fetch IoT credentials");
      credentialsCache = res.data;
      credentialsPromise = null;
      return credentialsCache;
    })
    .catch((err) => {
      credentialsPromise = null;
      throw err;
    });

  return credentialsPromise;
}

let socket = null;
let reconnectTimer = null;
let refreshTimer = null;
let pingTimer = null;
let isConnecting = false;
let isIntentionalDisconnect = false;

// filter -> Set<callback>
const subscriptions = new Map();

function topicMatches(filter, topic) {
  const f = filter.split("/");
  const t = topic.split("/");
  for (let i = 0; i < f.length; i += 1) {
    if (f[i] === "#") return true;
    if (f[i] !== "+" && f[i] !== t[i]) return false;
  }
  return f.length === t.length;
}

function dispatch(message) {
  const topics = message.topics || [];
  subscriptions.forEach((callbacks, filter) => {
    // Check if the subscribed filter matches ANY of the topics in the message
    const isMatch = topics.some((topic) => topicMatches(filter, topic));
    if (isMatch) {
      callbacks.forEach((cb) => cb(message));
    }
  });
}

function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function disconnect() {
  isIntentionalDisconnect = true;
  stopPing();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (socket) {
    socket.close();
    socket = null;
  }
}

function scheduleReconnect() {
  if (isIntentionalDisconnect || subscriptions.size === 0) {
    return;
  }
  if (reconnectTimer) {
    return; // Already scheduled
  }
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, 5000);
}

async function connect() {
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }
  if (isConnecting) return;
  isConnecting = true;
  isIntentionalDisconnect = false;

  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  try {
    const creds = await getCredentials();
    const { realtime_wss_url: wssUrl, token } = creds;
    if (!wssUrl) {
      // eslint-disable-next-line no-console
      console.warn("[WSS] Backend did not return realtime_wss_url");
      isConnecting = false;
      scheduleReconnect();
      return;
    }

    const connectUrl = `${wssUrl}?token=${encodeURIComponent(token)}`;

    socket = new WebSocket(connectUrl);

    socket.onopen = () => {
      isConnecting = false;

      // Start ping to keep API Gateway connection alive (every 4.5 minutes)
      stopPing();
      pingTimer = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ action: "ping" }));
        }
      }, 4.5 * 60 * 1000);

      // Refresh connection at 50 min to avoid token expiry
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
      refreshTimer = setTimeout(() => {
        credentialsCache = null;
        disconnect();
        isIntentionalDisconnect = false;
        connect();
      }, 50 * 60 * 1000);
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.event === "pong") return; // Ignore ping replies
        dispatch(payload);
      } catch {
        // ignore
      }
    };

    socket.onerror = (err) => {
      // eslint-disable-next-line no-console
      console.error("[WSS] Error:", err);
    };

    socket.onclose = () => {
      isConnecting = false;
      stopPing();
      socket = null;
      credentialsCache = null;

      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }

      scheduleReconnect();
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[WSS] Could not fetch credentials or connect:", err);
    isConnecting = false;
    credentialsCache = null;
    scheduleReconnect();
  }
}

function subscribe(topic, callback) {
  if (!subscriptions.has(topic)) {
    subscriptions.set(topic, new Set());
  }
  subscriptions.get(topic).add(callback);

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    connect();
  }

  return () => {
    const callbacks = subscriptions.get(topic);
    if (!callbacks) return;
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      subscriptions.delete(topic);
    }
  };
}

const wsRealtimeService = { getCredentials, subscribe, disconnect };
export default wsRealtimeService;
